import { useEffect, useRef } from "react";
import { useSubmitAnalyticMutation } from "redux/queries/analytics";
import {
  trackEvent as alTrackEvent,
  trackPageView as alTrackPageView,
  toTag,
} from "services/analytics.service";
import { parseTerminalId } from "utils/parseTerminalId";

// Realms whose analytics events are sent to the Analytics tool via the SDK.
// Every other realm keeps the legacy PF-backend-only mechanism untouched.
const SDK_ANALYTICS_REALMS = ["dxb", "dwc"];

const usesSdkAnalytics = () =>
  SDK_ANALYTICS_REALMS.includes(window._env_?.REACT_APP_REALM);

// Optional page dimensions the AL SDK aggregates on. The PF backend derives
// these server-side (flight lookup / URL params); the frontend forwards the
// ones a page knows so the Analytics tool can slice visitors by them.
const PAGE_DIMENSIONS = {
  key: "PF page key",
  flight_type: "arrival | departure",
  terminal: "Terminal id",
  area: "Smart-gate area",
  sub_area: "Smart-gate sub-area",
};

const buildDimensionTags = (data = {}) =>
  Object.entries(PAGE_DIMENSIONS).reduce((tags, [field, description]) => {
    // Terminal comes from an untrusted URL param — keep only the leading number
    // so share/tracking junk never reaches the Analytics tool (defence in depth;
    // callers should already sanitize, but this guards any that don't).
    const value =
      field === "terminal" ? parseTerminalId(data[field]) : data[field];
    if (value != null && value !== "") {
      tags[field] = toTag(String(value), description);
    }
    return tags;
  }, {});

// The realm is also attached globally at SDK bootstrap; we forward it here too
// (the user's requirement), but only when present so we never clobber the
// global tag with a null on an unready `window._env_`.
const realmTag = () => {
  const realm = window._env_?.REACT_APP_REALM;
  return realm ? { realm: toTag(realm, "The realm/airport code") } : {};
};

const useTrackPageAnalytics = (
  initialAnalyticData,
  { isFirstTimeOpened, uniqueDeviceIdentifier } = {
    isFirstTimeOpened: null,
    uniqueDeviceIdentifier: null,
  }
) => {
  const [submitAnalytic, { error }] = useSubmitAnalyticMutation();
  const lastSentURI = useRef(null);

  const path = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search).toString();
  const URI = path + searchParams;

  useEffect(() => {
    // Check if analytics data exists and the current URI hasn't been tracked yet
    if (initialAnalyticData && lastSentURI.current !== URI) {
      const markFirstOpen = () => {
        if (isFirstTimeOpened !== null) {
          sessionStorage.setItem("isFirstTimeOpened", "false");
          sessionStorage.setItem(
            "uniqueDeviceIdentifier",
            uniqueDeviceIdentifier
          );
        }
      };

      if (usesSdkAnalytics()) {
        // SDK-analytics realms: page views go only to the Analytics tool via
        // the SDK below. The PF backend ignores page-view hits on these realms,
        // so we skip the redundant call (scan_timing is the only backend flow).
        markFirstOpen();
      } else {
        // Legacy realms: the PF backend records the page-view hit.
        submitAnalytic({
          type: "hit",
          uri: URI,
          ...initialAnalyticData,
        }).then(markFirstOpen);
      }

      // Mirror to the AL SDK (gated by the same lastSentURI guard). Only the
      // SDK-analytics realms carry the enriched visitor dimensions; others
      // keep the pre-existing uri-only page view.
      alTrackPageView({
        uri: toTag(URI, "The SPA route URI being viewed"),
        ...(usesSdkAnalytics()
          ? { ...realmTag(), ...buildDimensionTags(initialAnalyticData) }
          : {}),
      });
      lastSentURI.current = URI;
    }
  }, [
    initialAnalyticData,
    URI,
    submitAnalytic,
    uniqueDeviceIdentifier,
    isFirstTimeOpened,
  ]);

  const sendAnalytics = (analyticData) => {
    // On SDK-analytics realms, general (click) events go only to the Analytics
    // tool via the SDK; the PF backend ignores them there, so we skip the
    // backend call. Other realms keep the legacy backend mechanism. (Passport
    // scan_timing is submitted separately in useScanAnalytics and always goes
    // to the backend, which forwards it to the Analytics tool.)
    if (usesSdkAnalytics() && analyticData?.type === "event") {
      alTrackEvent("pf_event", {
        key: toTag(analyticData.key, "PF interaction key"),
        name: toTag(analyticData.name, "Human-readable event name"),
        ...realmTag(),
        ...(analyticData.uri
          ? { uri: toTag(analyticData.uri, "Redirect URI") }
          : {}),
      });
      return;
    }
    submitAnalytic(analyticData);
  };

  if (error) {
    console.error("Could not send analytics: " + JSON.stringify(error));
  }

  return { sendAnalytics };
};

export default useTrackPageAnalytics;
